import React, {useEffect, useState} from "react";
import {QuestionInputProps} from "../Question";
import {AnswerStatus, MCColors, MCDisabledColor} from "../common/questionConstants";
import MultipleChoiceButton from "./MultipleChoiceButton";
import clsx from "clsx";
import SubmitButton from "../../common/SubmitButton";
import {AnswerDistributionLabel, Direction} from "../../common/AnswerDistributionLabel";
import MultipleChoiceLabel from "../common/MultipleChoiceLabel";
import {createUseStyles, useTheme} from "react-jss";
import {Theme} from "engage-client/theme";
import {Letters, TriviaStatus} from "../../../state/state";
import {MCQuestionDefinition} from "../../../state/questions";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import useMediaQuery from "engage-client/hooks/useMediaQuery";
import SendIcon from "../../common/SendIcon";

const SubmitButtonWidth = 80;
const SubmitOverlap = 38;
export const LabelWidth = SubmitButtonWidth - SubmitOverlap;

type PureMultipleChoiceInputProps = QuestionInputProps & {
	answerDistribution?: number[]
}

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
	buttonContainer: {
		display:"flex",
		flexDirection: "column",
		margin: [8,32],
		[theme.breakpoints.down("xs")]: {
			margin: 0,
		},
		[theme.breakpoints.up("lg")]: {
			flexDirection: "row",
			flexWrap: "wrap"
		}
	},
	optionSpacer: {
		boxSizing: "border-box",
		marginBottom: 24,
		padding: [0,24],
		[theme.breakpoints.between("sm","md")] : {
			padding: [0,40],
		},
		[theme.breakpoints.up("lg")]: {
			width: "50%"
		}
	},
	optionContainer: {
		boxSizing: "border-box",
		display: "flex",
		border: "solid",
		borderWidth: 3,
		borderRadius: 7,
		overflow: "hidden",
		//https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b
		"-webkit-mask-image": "-webkit-radial-gradient(white, black)",
		transition: "transform .25s ease-in-out",
		[theme.breakpoints.down("md")]: {
			borderWidth: 1
		}
	},
	option: {
		display: "flex",
		position: "relative",
		transition: "transform .25s ease-in-out",
		width: "100%"
	},
	optionReveal: {
		[theme.breakpoints.down("md")] : {
			transform: `translateX(-${SubmitButtonWidth - SubmitOverlap}px)`,
		}
	},
	mcButtonContainer: {
		flex: "1 0 100%",
		overflow: "hidden"
	},
	inlineSubmit: {
		border: "none",
		cursor: "pointer",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		width: SubmitButtonWidth,
		position:"absolute",
		left: "100%",
		top: 0,
		bottom: 0,
		margin: 0,
		transition: "left .25s ease-in-out",
		backgroundColor: theme.questionSubmit.matchInlineStyle ? theme.questionSubmit.background : "rgba(239,237,243, 1)",
		[theme.breakpoints.up("lg")]: {
			display: "none"
		},
		fontWeight: theme.questionSubmit.matchInlineStyle ? theme.questionSubmit.fontWeight : 400,
	},
	inlineSubmitReveal: {
		left: `calc(100% - ${SubmitOverlap}px)`
	},
	submit: {
		margin: [16, "auto"],
		[theme.breakpoints.down("md")]: {
			display: "none"
		},
		"&:disabled": {
			opacity: .6
		}
	},
	revealText: {
		margin:[8, 32, 0]
	},
	countContainer: {
		position: "relative",
		marginLeft: LabelWidth + 3,
		marginRight: 3,
		[theme.breakpoints.down("md")]: {
			marginLeft: LabelWidth + 1,
			marginRight: 1
		}
	},
	feedbackText: {
		marginBottom: 16
	}
}));

export function PureMultipleChoiceInput(props:PureMultipleChoiceInputProps) {
	const {state, question, onSubmit, response, pointsScored, submitted, answerDistribution} = props;
	const theme = useTheme<Theme>();
	// Track the currently selected index for answer submitting purposes.
	const [currentIndex, setCurrentIndex] = useState(-1);

	// Track the last index for animation purposes
	const [lastIndex, setLastIndex] = useState(-1);

	// Track when we're animating the submit button out.
	const [isRemovingSubmit, setIsRemovingSubmit] = useState(true);

	const isMdOrLess = useMediaQuery(theme.breakpoints.down("md"));
	const classes = useStyles();
	let totalSubmissions: number|undefined;

	useEffect(() => {
		const index = response?.value as number;
		setCurrentIndex(index);
	}, [response]);

	const handleClick = (index: number) => {
		if(lastIndex < 0) {
			setLastIndex(index);
		}
		setCurrentIndex(index);
	};

	const onSubmitButton = () => {
		if(onSubmit) {
			onSubmit(currentIndex);
		}
	};

	useEffect(() => {
		if (state === TriviaStatus.QuestionStart) {
			setCurrentIndex(-1);
			setLastIndex(-1);
			setIsRemovingSubmit(true);
		} else if(state === TriviaStatus.QuestionSummary) {
			//Remove selection if they didn't submit it in time
			if(!submitted) {
				setCurrentIndex(-1);
				setLastIndex(-1);
			}
		}
	}, [state, submitted]);

	//Guaranteed to be an MCQuestionDefinition based on Question.tsx logic
	const mcQuestion = question as MCQuestionDefinition;

	const percentages = new Array(mcQuestion.options.length).fill(100);

	if(answerDistribution) {
		totalSubmissions = answerDistribution.reduce((acc, val) => acc + val, 0);
		for(let i = 0; i < percentages.length; i++) {
			if(totalSubmissions) {
				percentages[i] = (answerDistribution[i] / totalSubmissions) * 100;
			} else {
				percentages[i] = 0;
			}
		}
	}

	const showDistribution = answerDistribution ?? false;

	//This will take up the line height without showing anything so that things don't shift when we add the feedback.
	let feedbackText = "\u00a0";
	if(submitted && typeof response !== "undefined" && state === TriviaStatus.QuestionAnswer) {
		if(response.value === mcQuestion.correctIndex && pointsScored) {
			feedbackText = `Congratulations, you scored ${pointsScored} points!`;
		} else if(response.value !== mcQuestion.correctIndex) {
			feedbackText = `Sorry, the answer is ${Letters[mcQuestion.correctIndex!]}.`;
		}
	}

	const disableSubmit = submitted || currentIndex === -1 || typeof currentIndex === "undefined";

	return <div data-qa="mc-input">
		<DisplayText className={classes.feedbackText} variant={TextVariant.s20med} align="center" color={theme.multipleChoice.feedbackColor}>
			{feedbackText}
		</DisplayText>
		<div className={classes.buttonContainer} data-qa="mc-buttons">
			{mcQuestion.options.map((option, index) => {
				const colors = MCColors(theme);
				let color = colors[index % colors.length];
				let selected = index === currentIndex;
				let status = AnswerStatus.unknown;
				let disabled = false;
				let className = "";
				let animationOffset = "0";

				// Switch for choice color:
				switch(state) {
					case TriviaStatus.QuestionSubmit:
						if(!submitted && isMdOrLess) {
							animationOffset = `${SubmitOverlap}px`;
						}
						break;
					case TriviaStatus.QuestionSummary:
						if(answerDistribution && totalSubmissions) {
							animationOffset = `${100 - (answerDistribution[index] / totalSubmissions) * 100}%`;
							className = "checked";
						}
						break;
					case TriviaStatus.QuestionAnswer:
						if(index === mcQuestion.correctIndex) {
							className = "checked";
							status = AnswerStatus.correct;
						} else {
							status = AnswerStatus.incorrect;
							color = MCDisabledColor(theme);
						}
						if(answerDistribution && totalSubmissions) {
							animationOffset = `${100 - (answerDistribution[index] / totalSubmissions) * 100}%`;
							className = "checked";
						}
						break;
				}

				if (submitted || state !== TriviaStatus.QuestionSubmit) {
					disabled = true;
				}

				let showInlineSubmit = state === TriviaStatus.QuestionSubmit && (!submitted || isRemovingSubmit);

				let label = <MultipleChoiceLabel disabled={disabled} selected={selected}
								 status={status} color={color}>{Letters[index]}</MultipleChoiceLabel>;

				return <div key={index} className={classes.optionSpacer}>
						<div className={classes.optionContainer} style={{borderColor: color, background: color}}>
							<div className={clsx(classes.option, !submitted && selected && classes.optionReveal)}
									onTransitionEnd={() => {
										if(lastIndex === index) {
											setLastIndex(currentIndex);
										}
						}}>
							<div className={classes.mcButtonContainer}  data-qa="mc-button" data-value={index}>
								<MultipleChoiceButton
									className={className}
									animationOffset={animationOffset}
									color={color}
									label={label}
									labelWidth={LabelWidth}
									disabled={disabled}
									checked={selected}
									onChange={() => handleClick(index)}>{option}</MultipleChoiceButton>
							</div>

							{ showInlineSubmit ? <button onClick={onSubmitButton}
									style={{color: theme.questionSubmit.matchInlineStyle ? "black" : color}}
									className={clsx(classes.inlineSubmit,
										currentIndex === index && // If we're revealing the submit button on this index
										(!isRemovingSubmit || !submitted) && // and we're not removing submit while we're not submitted
										classes.inlineSubmitReveal)}
									onTransitionEnd={() => {
										if (submitted) {
											setIsRemovingSubmit(false);
										}}
									}>
								{(!theme.questionSubmit.matchInlineStyle || theme.questionSubmit.icon) && <SendIcon/>}
								<span>Submit</span>
							</button> : undefined }
						</div>
					</div>
					{showDistribution ? <div className={classes.countContainer}>
						<AnswerDistributionLabel color={color} direction={Direction.Up} style={{left: `${percentages[index]}%`}}>
							<DisplayText variant={TextVariant.s20med} lineHeight={1.1}>{answerDistribution![index]}</DisplayText>
						</AnswerDistributionLabel>
					</div> : undefined }
				</div>;
			})
			}
			{state === TriviaStatus.QuestionSubmit ?
				<SubmitButton disabled={disableSubmit} onClick={onSubmitButton} className={classes.submit}>Submit</SubmitButton>
				: undefined }

		</div>
	</div>;
}

export default function MultipleChoiceInput(props:QuestionInputProps) {
	const {state, question, onSubmit, submitted, response, pointsScored, aggregateResponses} = props;

	let answerDistribution:number[]|undefined = undefined;

	//Guaranteed to be an MCQuestionDefinition based on Question.tsx logic
	const mcQuestion = question as MCQuestionDefinition;

	if((state === TriviaStatus.QuestionSummary || state === TriviaStatus.QuestionAnswer) && aggregateResponses) {
		const optionCounts = aggregateResponses.optionCounts;
		answerDistribution = new Array(mcQuestion.options.length).fill(0);
		for(let i = 0; i < mcQuestion.options.length; i++) {
			answerDistribution[i] = optionCounts[i] ?? 0;
		}
	}

	return <PureMultipleChoiceInput state={state}
									question={question}
									onSubmit={onSubmit}
									submitted={submitted}
									answerDistribution={answerDistribution}
									response={response}
									pointsScored={pointsScored}
	/>;
}
